import React from "react";
import { View } from "react-native";
import dayjs from "dayjs";
import { Link, Panel, Pill, Text, theme } from "@clearabee/ui-library-native";
import { Table } from "@clearabee/ui-library/src/Composite/Table/Table";
import { SkeletonLoader } from "components/SkeletonLoader/SkeletonLoader";
import { Rect } from "react-native-svg";
import { useCatalogue } from "@clearabee/ui-sdk";
import { useTranslation } from "react-i18next";
import { styles } from "./JobView.styles";
import { Image } from "components/Image/Image";
import placeholderImage from "lib/images/placeholder.png";
import { ExtendedJobViewProps } from "./types";
import { showDeliveryText } from "lib/utils";

export const LargeScreenView = ({
  job,
  isLoading,
  address,
  categorySku,
  itemsCollapsed,
  collapseItems,
  isCollapsable,
  showTrain,
}: ExtendedJobViewProps): React.ReactElement => {
  const [translate] = useTranslation();
  const { defaultCatalogue } = useCatalogue();

  return (
    <>
      {isLoading && (
        <Panel styles={{ padding: 0 }}>
          <SkeletonLoader
            speed={1}
            backgroundColor={theme.colors.light.base}
            foregroundColor={`${theme.colors.greyscale.lighter}00`}
            height={304}
            width="100%"
          >
            <Rect x="0" y="0" rx="4" ry="4" width="100%" height="100%" />
          </SkeletonLoader>
        </Panel>
      )}
      {!isLoading && job && (
        <Panel>
          <Table style={{ margin: 0 }}>
            <colgroup>
              <col style={{ width: "20%" }} />
              <col style={{ width: "20%" }} />
              {(job.timePreference || job.timeslotJson) && (
                <col style={{ width: "20%" }} />
              )}
              <col style={{ width: "auto" }} />
              <col style={{ width: "10%" }} />
              {!showTrain && <col style={{ width: "10%" }} />}
            </colgroup>
            <Table.Header
              headings={[
                translate(
                  showDeliveryText(categorySku)
                    ? "common.deliveryAddress"
                    : "common.collectionAddress",
                ),
                translate(
                  showDeliveryText(categorySku)
                    ? "common.deliveryDate"
                    : "common.collectionDate",
                ),
                ...(job.timePreference || job.timeslotJson
                  ? [translate("common.collectionTime")]
                  : []),
                translate("common.products"),
                translate("common.quantity"),
                ...(!showTrain ? [translate("common.status")] : []),
              ]}
              fontSize="xsmall"
              css={{
                // center align quantity and status headings
                [`th:nth-last-of-type(-n+${showTrain ? 1 : 2})`]: {
                  textAlign: "center",
                },
              }}
            />
            <Table.Body>
              <Table.Row styles={{ border: "none" }}>
                <Table.Cell valign="top">
                  <Text fontSize="small">{address}</Text>
                </Table.Cell>
                <Table.Cell valign="top">
                  <Text fontSize="small">
                    {dayjs.utc(job?.date).format("ddd DD MMM YYYY")}
                  </Text>
                </Table.Cell>
                {(job.timePreference || job.timeslotJson) && (
                  <Table.Cell valign="top">
                    <Text fontSize="small">
                      {job.timePreference === "Any" &&
                        translate(
                          "hwa.screens.dashboard.userDashboard.timeslotPlaceholder",
                        )}
                      {job.timePreference !== "Any" &&
                        job.timePreference !== "" &&
                        job.timePreference}
                      {job.timeslotJson &&
                        `${dayjs(`2020-01-01T${job.timeslotJson.from}`).format(
                          "ha",
                        )} - ${dayjs(
                          `2020-01-01T${job.timeslotJson.to}`,
                        ).format("ha")}`}
                    </Text>
                  </Table.Cell>
                )}
                <Table.Cell valign="top">
                  {job.items
                    ?.slice(0, itemsCollapsed ? 3 : undefined)
                    .map((item) => (
                      <View
                        key={`${item.id}-product`}
                        style={styles.largeProduct}
                      >
                        <Image
                          source={
                            defaultCatalogue?.items.find(
                              ({ sku }) => sku === item.sku,
                            )?.img ?? placeholderImage
                          }
                          style={{
                            width: 50,
                            height: 50,
                            position: "absolute",
                          }}
                          resizeMode="contain"
                        />
                        <Text
                          fontSize="small"
                          styles={{ marginLeft: theme.spacing.xlarge3 }}
                        >
                          {item.title}
                        </Text>
                      </View>
                    ))}
                </Table.Cell>
                <Table.Cell valign="top">
                  {job.items
                    ?.slice(0, itemsCollapsed ? 3 : undefined)
                    .map((item) => (
                      <View
                        key={`${item.id}-quantity`}
                        style={styles.largeQuantity}
                      >
                        <Text fontSize="small">{item.qty}</Text>
                      </View>
                    ))}
                </Table.Cell>
                {!showTrain && (
                  <Table.Cell valign="top">
                    <Pill
                      color="negative"
                      styles={{
                        alignItems: "center",
                        top: -theme.spacing.xsmall,
                      }}
                    >
                      {job.status}
                    </Pill>
                  </Table.Cell>
                )}
              </Table.Row>
            </Table.Body>
          </Table>

          {isCollapsable && (
            <Link
              color="brand"
              styles={{ alignSelf: "flex-end" }}
              textStyles={theme.fontDefaults.small}
              underline
              onPress={() => collapseItems(!itemsCollapsed)}
            >
              {itemsCollapsed
                ? translate("common.showAll")
                : translate("common.hideItems")}
            </Link>
          )}
        </Panel>
      )}
    </>
  );
};
